// src/services/joinTypes.js
//
// SourceX Roadmap — PHASE 2: the SoA x eCRF join table.
//
// The roadmap's core equation is
//     SoA (what's due this visit) x eCRF (the real questions) = source for this visit
// and this file is the schema of the "x". A join table is a list of mappings,
// each one asserting: "SoA assessment A is captured by eCRF form F (and
// optionally, specifically by these fields of F)".
//
// Per roadmap §3, this is where the value AND the risk both concentrate. The
// two documents name the same thing differently ("Wt (kg)" vs "Body Weight"),
// so matching is inherently fuzzy — and a wrong match silently produces a
// source document that asks the wrong questions at a visit. Everything here is
// therefore built around ONE property: the join is REVIEWABLE, and a human's
// review survives.
//
// Framework-free (no Firebase, no React) so it is unit-testable and so the
// Cloud Function can hold the same contract. Mirrors protocolTypes.js /
// ecrfTypes.js conventions.
//
// ---------------------------------------------------------------------------
// WHERE THE PIPELINE LIVES vs WHAT THIS FILE IS
// ---------------------------------------------------------------------------
// CANDIDATE GENERATION (tokenizing, scoring, tiering) is server-side in
// functions/joinMatcher.js, next to the LLM call that resolves the residue.
// This file is the CLIENT-side half: the schema, factories for the review UI,
// validation, coverage reporting, and mergeJoinDecisions() — which is the piece
// that makes recomputation safe.
//
// The only overlap is `soaAssessmentKey` and its two dependencies (`normKey`,
// `stableHash`), ~3 lines, and it MUST stay in sync with
// functions/joinMatcher.js. Same arrangement Phase 1b already uses between
// ecrfTypes.js and functions/ecrfExtraction.js.

import { stableHash } from "./templateSchema";
import { normKey } from "./ecrfTypes";

export { normKey };

// Bump when the join schema changes in a way that should invalidate stored
// join tables. Independent of EXTRACTION_VERSION and ECRF_EXTRACTION_VERSION so
// the three pipelines version separately.
export const JOIN_SCHEMA_VERSION = 1;

// ---------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------
// The eCRF side already has deterministic ids (ecrfFormId / ecrfFieldId hashed
// from normalized names). The SoA side does NOT: `assessment_id` comes back
// from the model, so a re-parse can rename every row.
//
// That asymmetry is a real hazard, not a cosmetic one. Mappings are keyed by
// (soa key, form id); if the SoA key changed on every re-parse, then re-running
// extraction would orphan every mapping and a coordinator would have to
// re-review the entire join table to arrive at the answers they had already
// given. Roadmap §5 rule 2 ("cache the join") is only true if the cache key is
// stable.
//
// So the SoA key is derived the same way the eCRF ids are: hashed from the
// normalized assessment NAME. Same name, same key, forever, regardless of what
// the model called it this time.

/** Stable id for an SoA assessment row, derived from its name. */
export const soaAssessmentKey = (assessmentName) =>
  `sa_${stableHash(normKey(assessmentName))}`;

/** The primary key of a single mapping row. Both halves are deterministic. */
export const mappingKey = (soaKey, formId) => `${soaKey}|${formId}`;

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

// How a mapping was arrived at. Ordered cheapest/most-trustworthy first; this
// is also the audit trail the review UI shows ("why is this here?").
export const MATCH_METHODS = Object.freeze({
  EXACT: "exact", // normalized names are identical
  ALIAS: "alias", // matched through the abbreviation/synonym table
  FUZZY: "fuzzy", // token-similarity above the auto-match threshold
  LLM: "llm", // the model resolved it from the ambiguous residue
  MANUAL: "manual", // a human created this mapping in the review UI
});

export const MATCH_METHOD_VALUES = Object.freeze(Object.values(MATCH_METHODS));

// Methods a machine can produce. Anything else is a human's doing, and
// mergeJoinDecisions() treats the two differently.
const MACHINE_METHODS = Object.freeze([
  MATCH_METHODS.EXACT,
  MATCH_METHODS.ALIAS,
  MATCH_METHODS.FUZZY,
  MATCH_METHODS.LLM,
]);

export const MATCH_STATUS = Object.freeze({
  PROPOSED: "proposed", // machine's suggestion, awaiting review
  CONFIRMED: "confirmed", // a human said yes
  REJECTED: "rejected", // a human said no
});

export const MATCH_STATUS_VALUES = Object.freeze(Object.values(MATCH_STATUS));

// Below this, a proposal is shown but never treated as settled. Matches the
// LOW_CONFIDENCE_THRESHOLD convention in the other two type modules.
export const LOW_CONFIDENCE_THRESHOLD = 0.9;

// ---------------------------------------------------------------------------
// Factories
// ---------------------------------------------------------------------------

const clamp01 = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(1, v));
};

const uniqStrings = (a) =>
  [...new Set((Array.isArray(a) ? a : []).map((x) => String(x).trim()).filter(Boolean))];

/**
 * One assertion: this SoA assessment is captured by this eCRF form.
 *
 * `field_ids` is optional and narrowing, not defining. An SoA row is often a
 * whole form ("Vital Signs"), but sometimes a single question inside one
 * ("Weight" -> the Weight field of Vital Signs). Recording which fields matched
 * lets Phase 3 assemble the narrow case without pulling in the entire form,
 * while an empty list means "the whole form applies".
 */
export const makeJoinMapping = (init = {}) => {
  const soaKey = String(init.soa_key || soaAssessmentKey(init.soa_name || ""));
  const formId = String(init.form_id || "");
  const method = MATCH_METHOD_VALUES.includes(init.method)
    ? init.method
    : MATCH_METHODS.FUZZY;
  const status = MATCH_STATUS_VALUES.includes(init.status)
    ? init.status
    : MATCH_STATUS.PROPOSED;

  const m = {
    key: mappingKey(soaKey, formId),
    soa_key: soaKey,
    // Denormalized display names. The review UI must be able to render a row
    // without loading both source extractions, and an audit record of what the
    // human actually saw is worth more than the few bytes it costs.
    soa_name: String(init.soa_name || "").slice(0, 300),
    form_id: formId,
    form_name: String(init.form_name || "").slice(0, 300),
    field_ids: uniqStrings(init.field_ids).slice(0, 200),
    method,
    status,
    score: clamp01(init.score),
    // Why the matcher believes this. Shown verbatim in the review UI: a
    // coordinator confirming a fuzzy match needs to see the evidence, not just
    // a number. Kept short so the join doc stays small over hundreds of rows.
    rationale: String(init.rationale || "").slice(0, 400),
    reviewedBy: String(init.reviewedBy || ""),
  };

  // Set only when a human has acted, so "has this been touched?" is a simple
  // truthiness check rather than a comparison against a default.
  if (init.reviewedAt) m.reviewedAt = init.reviewedAt;
  return m;
};

/**
 * An SoA row the matcher could not place. Tracked as first-class data, NOT as
 * an absence: roadmap §7 says the residual 10% is confirming ambiguous rows, and
 * a row that silently has no mapping is indistinguishable from a row nobody has
 * looked at yet. These sort to the top of the review UI.
 */
export const makeUnmatched = (init = {}) => ({
  soa_key: String(init.soa_key || soaAssessmentKey(init.soa_name || "")),
  soa_name: String(init.soa_name || "").slice(0, 300),
  // Best rejected guesses, so the reviewer starts from a shortlist rather than
  // the full form inventory.
  candidates: (Array.isArray(init.candidates) ? init.candidates : [])
    .slice(0, 5)
    .map((c) => ({
      form_id: String(c?.form_id || ""),
      form_name: String(c?.form_name || "").slice(0, 300),
      score: clamp01(c?.score),
    })),
  reason: String(init.reason || "").slice(0, 300),
});

export const emptyJoinTable = () => ({
  study_id: "",
  protocol_id: "",
  ecrf_id: "",
  mappings: [],
  unmatched: [],
  warnings: [],
  stats: {},
  join_version: JOIN_SCHEMA_VERSION,
});

// ---------------------------------------------------------------------------
// Decision preservation — the reason this module exists
// ---------------------------------------------------------------------------

/**
 * Merge a freshly computed join table into the stored one, keeping human
 * decisions authoritative.
 *
 * Recomputation happens for boring reasons: an eCRF re-parse, a matcher
 * improvement, a protocol amendment. Every one of those would otherwise throw
 * away the coordinator review that is the entire justification for claiming 90%
 * instead of 100% (roadmap §7). The rules, and why each one is not the obvious
 * alternative:
 *
 *  1. CONFIRMED beats a recompute. If a human said yes, the mapping stays —
 *     with its human status, reviewer, and timestamp — even if the matcher no
 *     longer proposes it. The matcher is the thing being corrected here, not
 *     the human.
 *
 *  2. REJECTED is sticky. A re-proposed pair that a human already killed stays
 *     killed. Without this, every recompute resurrects the same wrong match and
 *     the reviewer plays whack-a-mole forever — the failure mode that makes
 *     people stop reviewing carefully.
 *
 *  3. A decision dies only when its ANCHOR dies. If the form no longer exists
 *     in the eCRF (or the assessment no longer exists in the SoA), the mapping
 *     is dangling and is dropped with a warning. Keeping it would let Phase 3
 *     assemble against a form_id that resolves to nothing.
 *
 *  4. MANUAL mappings are never dropped for absence from the recompute — a
 *     human authored them precisely because the matcher didn't find them.
 *
 *  5. Machine metadata (score, method, rationale) always refreshes to the new
 *     computation, even on a confirmed row. The human's verdict is preserved;
 *     their view of *why* should still reflect the current matcher.
 *
 * Pure: mutates neither argument.
 *
 * @param {object} previous stored join table (may be null/empty)
 * @param {object} next freshly computed join table
 * @param {object} anchors { formIds:Set|Array, soaKeys:Set|Array } currently
 *        existing ids. Omit either to skip that liveness check — callers that
 *        only recomputed one side shouldn't prune the other.
 */
export const mergeJoinDecisions = (previous, next, anchors = {}) => {
  const prev = previous || emptyJoinTable();
  const fresh = next || emptyJoinTable();
  const warnings = [...(fresh.warnings || [])];

  const asSet = (v) =>
    v == null ? null : v instanceof Set ? v : new Set(Array.isArray(v) ? v : []);
  const liveForms = asSet(anchors.formIds);
  const liveSoa = asSet(anchors.soaKeys);

  const isLive = (m) =>
    (!liveForms || liveForms.has(m.form_id)) && (!liveSoa || liveSoa.has(m.soa_key));

  const prevByKey = new Map(
    (prev.mappings || []).filter((m) => m && m.key).map((m) => [m.key, m])
  );
  const out = [];
  const usedPrev = new Set();

  // Pass 1 — every freshly proposed mapping, reconciled against any prior
  // decision on the same (assessment, form) pair.
  (fresh.mappings || []).forEach((m) => {
    const before = prevByKey.get(m.key);
    if (!before) {
      out.push(m);
      return;
    }
    usedPrev.add(m.key);

    if (before.status === MATCH_STATUS.PROPOSED) {
      out.push(m); // nobody has ruled; the new computation simply wins
      return;
    }
    // Rule 1 + 2 + 5: human verdict survives, machine metadata refreshes.
    out.push({
      ...m,
      status: before.status,
      reviewedBy: before.reviewedBy,
      ...(before.reviewedAt ? { reviewedAt: before.reviewedAt } : {}),
    });
  });

  // Pass 2 — prior mappings the recompute did not re-propose.
  prevByKey.forEach((before, key) => {
    if (usedPrev.has(key)) return;

    const humanTouched =
      before.status !== MATCH_STATUS.PROPOSED ||
      before.method === MATCH_METHODS.MANUAL;

    if (!humanTouched) return; // a stale machine guess; let it go quietly

    if (!isLive(before)) {
      // Rule 3. Loud, because a coordinator's confirmed decision is
      // disappearing and the cause (a re-parse that renamed a form) is exactly
      // the kind of thing that should be visible rather than inferred later.
      warnings.push({
        type: "dropped_dangling_mapping",
        message:
          `Dropped ${before.status} mapping "${before.soa_name}" -> ` +
          `"${before.form_name}": it no longer exists in the current extraction.`,
      });
      return;
    }

    // Rules 1 + 4: keep it, but say so — a confirmed mapping the matcher can no
    // longer reproduce is a signal the matcher regressed.
    if (before.method !== MATCH_METHODS.MANUAL) {
      warnings.push({
        type: "kept_unreproduced_mapping",
        message:
          `Kept ${before.status} mapping "${before.soa_name}" -> ` +
          `"${before.form_name}"; the current matcher no longer proposes it.`,
      });
    }
    out.push({ ...before });
  });

  // An SoA row is only genuinely unmatched if nothing survived for it. A
  // confirmed mapping carried over from pass 2 answers the question even when
  // this run's matcher came up empty.
  const answered = new Set(
    out.filter((m) => m.status !== MATCH_STATUS.REJECTED).map((m) => m.soa_key)
  );
  const unmatched = (fresh.unmatched || []).filter((u) => !answered.has(u.soa_key));

  return { ...fresh, mappings: out, unmatched, warnings };
};

// ---------------------------------------------------------------------------
// Reporting
// ---------------------------------------------------------------------------

/**
 * Where the join stands. This is the number that tells a coordinator how much
 * review is left, and the number that tells us whether the matcher is working
 * on real documents — the thing Phase 2 cannot be tuned without.
 */
export const joinCoverage = (table, totalAssessments) => {
  const mappings = table?.mappings || [];
  const live = mappings.filter((m) => m.status !== MATCH_STATUS.REJECTED);
  const total = Number.isFinite(totalAssessments)
    ? totalAssessments
    : new Set([...live.map((m) => m.soa_key), ...(table?.unmatched || []).map((u) => u.soa_key)])
        .size;

  const matched = new Set(live.map((m) => m.soa_key)).size;
  const confirmed = new Set(
    mappings.filter((m) => m.status === MATCH_STATUS.CONFIRMED).map((m) => m.soa_key)
  ).size;

  const byMethod = {};
  live.forEach((m) => {
    byMethod[m.method] = (byMethod[m.method] || 0) + 1;
  });

  return {
    totalAssessments: total,
    matched,
    confirmed,
    unmatched: Math.max(0, total - matched),
    needsReview: live.filter((m) => m.status === MATCH_STATUS.PROPOSED).length,
    lowConfidence: live.filter(
      (m) => m.status === MATCH_STATUS.PROPOSED && m.score < LOW_CONFIDENCE_THRESHOLD
    ).length,
    byMethod,
    matchedPct: total ? Math.round((matched / total) * 100) : 0,
  };
};

/** Review queue order: never-reviewed and least-certain first. */
export const reviewQueue = (table) => {
  const pending = (table?.mappings || []).filter(
    (m) => m.status === MATCH_STATUS.PROPOSED
  );
  return [...pending].sort((a, b) => a.score - b.score || a.soa_name.localeCompare(b.soa_name));
};

/**
 * Structural validation. Strict about anything that would corrupt addressing or
 * let Phase 3 assemble against a nonexistent form; permissive about content,
 * which is the reviewer's job. Same posture as validateEcrfExtraction.
 */
export const validateJoinTable = (table, anchors = {}) => {
  const problems = [];
  if (!table || typeof table !== "object") return ["Join table is missing or not an object."];
  if (!Array.isArray(table.mappings)) return ["mappings is not an array."];

  const asSet = (v) =>
    v == null ? null : v instanceof Set ? v : new Set(Array.isArray(v) ? v : []);
  const liveForms = asSet(anchors.formIds);
  const liveFields = asSet(anchors.fieldIds);

  const seen = new Set();
  table.mappings.forEach((m, i) => {
    const where = m?.soa_name ? `mapping "${m.soa_name}"` : `mapping[${i}]`;
    if (!m || typeof m !== "object") {
      problems.push(`${where} is not an object.`);
      return;
    }
    if (!m.soa_key) problems.push(`${where} has no soa_key.`);
    if (!m.form_id) problems.push(`${where} has no form_id.`);
    if (m.key !== mappingKey(m.soa_key, m.form_id)) {
      problems.push(`${where} has a key inconsistent with its soa_key/form_id.`);
    }
    // Duplicates would make a human's confirm/reject ambiguous — two rows for
    // one decision, and mergeJoinDecisions keyed by `key` would keep only one.
    if (seen.has(m.key)) problems.push(`${where} is a duplicate mapping.`);
    seen.add(m.key);

    if (!MATCH_METHOD_VALUES.includes(m.method))
      problems.push(`${where} has unknown method "${m.method}".`);
    if (!MATCH_STATUS_VALUES.includes(m.status))
      problems.push(`${where} has unknown status "${m.status}".`);
    if (liveForms && !liveForms.has(m.form_id))
      problems.push(`${where} points at unknown form_id "${m.form_id}".`);
    if (liveFields) {
      (m.field_ids || []).forEach((fid) => {
        if (!liveFields.has(fid))
          problems.push(`${where} points at unknown field_id "${fid}".`);
      });
    }
  });

  return problems;
};

/** Machine-produced methods, exported for the matcher and its tests. */
export { MACHINE_METHODS };
